import {
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import React, { ReactNode } from "react";
import { AUTURA_ASPHALT_DARK, AUTURA_ASPHALT_LIGHT } from "utils";
import { MoreVert } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getHighlightColorFromHex } from "context/themeContext";

export interface MenuOption {
  onClick: () => void;
  content: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
}

interface MoreOptionsButtonProps {
  menuOptions: MenuOption[];
  disabled?: boolean;
  color: string;
}

export const MoreOptionsButton = ({
  menuOptions,
  disabled = false,
  color,
}: MoreOptionsButtonProps): ReactNode => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleItemClick =
    (option: MenuOption) => (event: React.MouseEvent<HTMLLIElement>) => {
      event.stopPropagation();
      handleClose();
      option.onClick();
    };

  return (
    <React.Fragment>
      <Tooltip title={t("buttons.moreActions")}>
        <IconButton
          sx={{ my: 0, p: 0, "&:hover": { opacity: 0.9 } }}
          id="more-options-button"
          disabled={!menuOptions.length || disabled}
          onClick={handleMenuClick}
        >
          <MoreVert
            htmlColor={disabled ? AUTURA_ASPHALT_LIGHT : color}
            fontSize={"large"}
          />
        </IconButton>
      </Tooltip>
      <Menu
        id="more-options-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": `more-options-button`,
          sx: {
            py: 0,
            border: `1px solid ${AUTURA_ASPHALT_DARK}`,
          },
        }}
      >
        {menuOptions.map((option, index) => (
          <MenuItem
            key={index}
            onClick={handleItemClick(option)}
            disabled={option.disabled}
            divider
            sx={{
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: getHighlightColorFromHex(palette.primary.main),
              },
            }}
          >
            {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
            {option.content}
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};
